/**
 * Created by Ning on 2021-02-09.
 *
 * Super basic Symbol implementation that extends PIXI.Sprite
 *
 */

import {Serial} from "../Serial";
import {SerialSymbolState} from "./SerialSymbolConfig";
import {IParsedStackedSymbolName, StackedSymbol} from "../../core/reel/stackedsymbol/StackedSymbol";
import {IBaseSymbol} from "../../core/symbol/BaseSymbol";
import {MultiSprites} from "@nolimitcity/slot-game/bin/animation/MultiSprites";
import {gsap} from "gsap";
import {SerialGameAssets} from "../SerialGameAssets";
import {ReelPositionController} from "../reels/ReelPositionController";
import {BitmapCountUp} from "@nolimitcity/slot-game/bin/component/BitmapCountUp";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";

// import {RevealedSymbol} from "./RevealedSymbol";

export class SerialSymbol extends PIXI.Container implements IBaseSymbol {

    // the value is the offset from the top of the full image to the display center when masked.
    public static SYMBOL_DISPLAY_CENTER_Y:{ [symName:string]:number } = {
        L1 : 95,
        L2 : 94,
        L3 : 78,
        L4 : 94,
        L5 : 96,
        M1 : 66,
        M2 : 68,
        M3 : 36,
        M4 : 36,
        M5 : 48,
        S1 : 76,
        W : 86
    };
    public static noWinTint:number = 0x555555;

    public symbolWidth:number = ReelPositionController.DEFAULT_SYMBOL_WIDTH;
    public bottomPadding:number = 0;

    protected _multiplierFont:string = SerialGameAssets.BITMAP_FONT_NUDGE_MULTIPLIER;
    protected _multiplierSize:number = 30;
    protected _multiplierLetterSpacing:number = 0;

    public symbolName:string;
    public stackedSymbolName:IParsedStackedSymbolName;
    public reelId:number;
    public blockReelLandingSound:boolean = false;

    protected _symbolHeight:number;
    protected _currentState:SerialSymbolState
    protected _animation:MultiSprites;
    protected _centerPosition:PIXI.Point;
    protected _multiplierContainer:PIXI.Container;
    protected _multiplierText:PIXI.BitmapText;

    protected _frameTop:PIXI.Sprite;
    protected _frameBottom:PIXI.Sprite;

    private _textureHeight:string;

    public get symbolHeight():number {
        return this._symbolHeight;
    }

    public set symbolHeight(value:number) {
        if(value === this._symbolHeight) {
            return;
        }

        this._symbolHeight = value;
        this.updateSymbolHeight();
    }

    public get actualHeight():number {
        return this.symbolHeight;
    }

    public get symbolStep():number {
        return this.symbolHeight + this.bottomPadding;
    }

    constructor(symbolName:string, reelId:number, symbolHeight:number, initState:SerialSymbolState) {
        super();
        this.symbolName = symbolName;
        this.stackedSymbolName = StackedSymbol.parseStackedSymbolName(symbolName, reelId);
        this.reelId = reelId;
        this._symbolHeight = symbolHeight;
        this.initAnimation();
        // this.updateSymbolHeight();
        this.getStateAnimation(initState, {duration : 0, alpha : 1}, {duration : 0});
    }

    protected updateSymbolHeight():void {
        if(!this.transform) {
            return;
        }
        if(this._frameTop) {
            this._frameTop.y = this._centerPosition.y - this.actualHeight * 0.5;
        }
        if(this._frameBottom) {
            this._frameBottom.y = this._centerPosition.y + this.actualHeight * 0.5;
        }

        if(this.getTextureHeight() != this._textureHeight) {
            this.removeChild(this._animation);
            this._animation.destroy();
            this._animation = new MultiSprites();
            this._animation.position.set(this._centerPosition.x, this.getAssetsPositionY());
            this.addChild(this._animation);
            this.updateLayers();
            this.getStateAnimation(this._currentState,
                {duration : 0.01, alpha : 1},
                {duration : 0.01, alpha : 0});
        } else {
            this._animation.position.y = this.getAssetsPositionY();
        }
    }
    private createWinWays(comb: number): PIXI.Text {
        const winWays: PIXI.Text = new PIXI.Text(comb + " " + SlotGame.apiPlugin.translations.translate(comb > 1 ? "Ways" : "Way"), new PIXI.TextStyle({
            fill: `#a68000`,
            fillGradientStops: [10],
            fontSize: 15,
            fontWeight: `bolder`,
            letterSpacing: 1,
            lineJoin: `round`,
            miterLimit: 0,
            stroke: `#030303`,
            strokeThickness: 8,
        }));
        winWays.visible = false;
        winWays.position.y = 35;
        if (winWays.width >= this.symbolWidth - 50) {
            let ftSize: number = winWays.style.fontSize;
            while (winWays.width >= this.symbolWidth - 50) {
                winWays.style.fontSize = ftSize;
                --ftSize;
            }
        }
        winWays.pivot.set(winWays.width/2,winWays.height/2);
        return winWays;
    }

    private createCountUp(winAmount: number): BitmapCountUp {
        const countUp: BitmapCountUp = new BitmapCountUp({
            fontName: SerialGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 20,
            tint: 0xffffff,
        });
        countUp.visible = false;
        countUp.position.y = -10;
        countUp.getCountUpAnimation(winAmount, winAmount, 0.002);
        if (countUp.width >= this.symbolWidth - 20) {
            let ftSize: number = countUp.fontSize;
            while (countUp.width >= this.symbolWidth - 20) {
                countUp.fontSize = ftSize;
                --ftSize;
            }
        }
        return countUp;
    }

    get individualWinWaysAmountTL(): gsap.core.Tween[] {
        return this._individualWinWaysAmountTL;
    }
    private _individualWinWaysAmountTL: gsap.core.Tween[] = [];

    public showWinAndWays(durr: number, winAmount: number, comb: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                this._individualWinWaysAmountTL = [];
                this.removeChild(winAmountCountUp);
                this.removeChild(winWays);
                winAmountCountUp.destroy();
                winWays.destroy();
            }
        });
        const winAmountCountUp: BitmapCountUp = this.createCountUp(winAmount),
            winWays: PIXI.Text = this.createWinWays(comb);
        this.addChild(winAmountCountUp, winWays);
        const countUpMoveTL: gsap.core.Tween = gsap.to(winAmountCountUp, {
                y: winAmountCountUp.y - 30,
                duration: durr
            }).pause(),
            winWaysMoveTL: gsap.core.Tween = gsap.to(winWays, {
                y: winWays.y - 30,
                duration: durr
            }).pause();
        this._individualWinWaysAmountTL.push(countUpMoveTL, winWaysMoveTL);
        tl.add(()=>{
            winAmountCountUp.visible=true;
            winWays.visible=true;
        })
        tl.add([countUpMoveTL.play(), winWaysMoveTL.play()]);
        return tl;
    }

    protected initAnimation():void {
        this._centerPosition = this.getCenterPosition();
        this._animation = new MultiSprites();
        this._animation.position.set(this._centerPosition.x, this.getAssetsPositionY());
        this.addChild(this._animation);

        if(this.symbolName === "E" || this.symbolName.includes("S")) {
            return;
        }

        const frameFolderName = SerialSymbol.getSymbolFramePath(this.symbolName);
        this._frameTop = new PIXI.Sprite(Serial.assetLoader.getTextures(`frames/${frameFolderName}/top`)[0]);
        this._frameTop.anchor.set(0.5, 0);
        this._frameTop.y = this._centerPosition.y - this.actualHeight * 0.5;
        this._frameBottom = new PIXI.Sprite(Serial.assetLoader.getTextures(`frames/${frameFolderName}/bottom`)[0]);
        this._frameBottom.anchor.set(0.5, 1);
        this._frameBottom.y = this._centerPosition.y + this.actualHeight * 0.5;
    }

    public static getSymbolFramePath(symName:string):string {
        if(symName.includes("L")) {
            return "L";
        }
        if(symName.includes("M")) {
            return "M";
        }
        if(symName.includes("S1")) {
            return "S1";
        }
        if(symName.includes("S2")) {
            return "S2";
        }
        if(symName.includes("WXN")) {
            return "WXN";
        }
        if(symName === "X") {
            return "X";
        }
        // W,
        return "W";
    }

    protected getAssetsPositionY():number {
        return this._centerPosition.y;
    }

    protected getCenterPosition():PIXI.Point {
        return new PIXI.Point(0, 0);
    }

    public getStateAnimation(state:SerialSymbolState, inVars:gsap.TweenVars, outVars:gsap.TweenVars, frames?:number[]):TimelineLite {
        this.createState(state);
        this._currentState = state;
        if(this._frameTop) {
            this._frameTop.visible = state != SerialSymbolState.SPIN;
        }
        if(this._frameBottom) {
            this._frameBottom.visible = state != SerialSymbolState.SPIN;
        }

        const tl = gsap.timeline();
        tl.add([
            this._animation.playSprite(state, inVars, outVars, frames),
            this.getStateColorChangeAnimation(this.getStateTint(state))
        ]);
        return tl;
    }

    protected getStateTint(state?:string):number {
        if(!state) {
            state = this._animation.currentSpriteName;
        }

        return state === SerialSymbolState.NO_WIN ? SerialSymbol.noWinTint : 0xffffff;
    }

    protected getStateColorChangeAnimation(tint:number):TimelineLite {
        // No need to tint wildPath since it can only be no win state in reel 5 inside the reel frame, not inside symbol.
        const tl = gsap.timeline();
        if(this._multiplierText) {
            tl.add(() => {this._multiplierText.tint = tint}, 0);
        }
        if(this._frameTop) {
            tl.add(() => {this._frameTop.tint = tint}, 0);
            // tl.add(gsap.to(this._frameTop, {duration : tintDuration, pixi : {saturation : tintSaturation}}), 0);
        }
        if(this._frameBottom) {
            tl.add(() => {this._frameBottom.tint = tint}, 0);
            // tl.add(gsap.to(this._frameBottom, {duration : tintDuration, pixi : {saturation : tintSaturation}}), 0);
        }

        return tl;
    }

    protected createState(state:SerialSymbolState):void {
        let updateLayer = false;
        if(!this._animation.sprites[state]) {
            this._animation.addSprites({
                name : state,
                textures : this.getStateTexture(state),
                anchor : new PIXI.Point(0.5, 0.5)
            });
            if(state === SerialSymbolState.NO_WIN) {
                this._animation.sprites[state].tint = SerialSymbol.noWinTint;
            }
            updateLayer = true;
        }
        if(updateLayer) {
            this.updateLayers();
        }
    }

    protected updateLayers():void {
        if(this._frameTop) {
            this.addChild(this._frameTop);
        }
        if(this._frameBottom) {
            this.addChild(this._frameBottom);
        }
        if(this._multiplierContainer) {
            this.addChild(this._multiplierContainer);
        }
    }

    protected getStateTexture(state?:SerialSymbolState):PIXI.Texture[] {
        let texturePath = `${this.symbolName}/${this.symbolName}`;

        if(this.symbolName.includes("L") || this.symbolName.includes("M")) {
            this._textureHeight = this.getTextureHeight();
        } else {
            this._textureHeight = ``;
        }
        texturePath += this._textureHeight;

        switch(state) {
            case SerialSymbolState.SPIN:
            case SerialSymbolState.LAND:
            case SerialSymbolState.WIN:
                texturePath += `_${state}`;
                break;
            default:
        }

        return Serial.assetLoader.getTextures(texturePath);
    }

    protected getTextureHeight():string {
        if(this.symbolHeight > 150) {
            return ``;
        }
        if(this.symbolHeight > 100) {
            return `_M`;
        }
        return `_S`;
    }

    public onRemove():void {
        this.destroy({children : true});
    }

    public getGlobalPosition():PIXI.Point {
        if(this.parent) {
            return new PIXI.Point(this.position.x + this.parent.x,
                this.position.y + this.parent.y);
        }
        return this.position;
    }

    public showMultiplier(text:string):gsap.core.Animation {
        this.createMultiplier();
        this._multiplierText.text = text;
        this.addChild(this._multiplierContainer);
        return gsap.fromTo(this._multiplierContainer.scale, {x : 1.3, y : 1.3}, {x : 1, y : 1, duration : 0.2});
    }

    protected createMultiplier():void {
        if(this._multiplierContainer) {
            return;
        }

        this._multiplierContainer = new PIXI.Container();
        this._multiplierContainer.position.set(this._centerPosition.x, this._centerPosition.y);

        this._multiplierText = new PIXI.BitmapText("", {
            fontName : this._multiplierFont,
            fontSize : this._multiplierSize,
            tint : 0xffffff
        });
        (<PIXI.Point>this._multiplierText.anchor).set(0.5);
        this._multiplierText.letterSpacing = this._multiplierLetterSpacing;
        this._multiplierContainer.addChild(this._multiplierText);

        this.addChild(this._multiplierContainer);
    }

    public getLandingState():SerialSymbolState {
        return SerialSymbolState.NORMAL;
    }
}

